
var _comUtilsDateCache = {};
/**
 * `com-utils`
 *
 *  Utils shared across components and SPAs
 */
export class COMUtils {
  constructor() {
  }

  /**
   * @desc get Cookie
   */
  static getCookie(cookieName) {
    let cookieKey = cookieName + "=";
    let ca = document.cookie.split(';');
    for (let i = 0;i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == ' ') c = c.substring(1, c.length);
      if (c.indexOf(cookieKey) === 0) {
        return c.substring(cookieKey.length,c.length);
      }
    }
    return null;
  }

  /**
   * @desc Get Auth Token from pepcomUI
   *
   * @return {object} token with access token and swid
   */
  static getPepcomGuestToken() {
    let pepJwtToken = CMEUtil.getCookie('pep_jwt_token');
    let obj = {};

    if (pepJwtToken != null) {
      try {
        let parseJwt = JSON.parse(atob(pepJwtToken.split('.')[1]));
        obj = {
          accessToken: parseJwt.disney.token.access_token,
          swid: parseJwt.disney.token.swid
        };
      } catch (e) {
        console.log(e);
      }
    }
    return obj;
  }

  /**
   * @desc Replace keys for values in content
   * @param {Array} keys
   * @param {Array} values
   * @param {Object[]} content
   * @return {String}
   */
  static replaceTokenKeys(keys, values, content) {
    if (keys && values && keys.length > 0 && values.length > 0 && keys.length === values.length && content) {
      let updatedContent = content;
      for (let i = 0; i < keys.length; i++) {
        updatedContent = this.replaceToken(keys[i], values[i], updatedContent);
      }
      return updatedContent;
    } else {
      return '';
    }
  }

  /**
   * @desc Replace token in content
   * @param {String} token
   * @param {String} price
   * @param {Object[]} content
   * @return {String}
   */
  static replaceToken(token, price, content) {
    if (content) {
      let updatedContent = content;
      content.split(" ").forEach(string => {
        if (string.includes(token)) {
          updatedContent = updatedContent.replace(token, price);
        }
      });
      return updatedContent;
    } else {
      return '';
    }
  }

  /**
   * @desc Format 24 hours time to 12 hours time
   * @param {String} time ex: '13:00'
   * @param {String} date date string, (today's date used if none provided)
   * @param {String} locale ex: 'en-us'
   * @return {String} ex: '1:00 PM'
   */
  static getTimeFormat(time, date, locale = COMUtils.getLocale()) {
    const arrayTime = time.split(':');
    let entryDate = COMUtils.getDateObject(date);
    entryDate.setHours(arrayTime[0], arrayTime[1]);

    return entryDate.toLocaleTimeString(locale, {hour: '2-digit', minute:'2-digit'});
  }

  /**
   * @desc `getLocale` // en-us, es-ar based on localeCookie_jar_aka or marketing_code
   * assumption is that there can never be both cookies available at the same time
   * @return {String} locale
   */
  static getLocale() {
    let localeCookie = COMUtils.getCookie('languageSelection_jar_aka');
    let locale = (localeCookie) ?
      JSON.parse(localeCookie).preferredLanguage.replace(/_/, '-').toLowerCase() :
      'en-us';

    return locale;
  }

  /**
   * @desc compute startDate based on startDate using an ISO8601 formatted string
   * @param {String} start date, ex: 2017-10-20
   * @param {Boolean} forceUTCMidnight
   * @return {Date} start date
   */
  static getDateObject(dateString, forceUTCMidnight = false) {

    // if time not present, add default time
    if (!!dateString) {
      if (dateString.length === 10) {
        dateString += 'T00:00:00';
      } else {
        dateString = dateString.split('T')[0] + 'T00:00:00';
      }
      if (forceUTCMidnight) {
        dateString += 'Z';
      }
    }

    let returnDate;

    if (!forceUTCMidnight && !!dateString) {

      // use cached date string if available
      if (_comUtilsDateCache[dateString]) {
        return new Date(_comUtilsDateCache[dateString]);
      }

      // create new date for current date string
      returnDate = new Date(dateString);

      let dateStringToCreate = dateString;

      let timeOffset = returnDate.toLocaleTimeString({}, { hour12: false }).replace(/[^ -~]/g, '');
      // Workaround to fix IOS returning UTC time when new Date() is created
      if (timeOffset !== '00:00:00' && timeOffset !== '0:00:00') {

        // get locale date to check how time offset needs to be calculated
        const locale = {};
        let dateCompare = returnDate.toLocaleDateString(locale, { month: '2-digit' }) + '-' +
          returnDate.toLocaleDateString(locale, { day: '2-digit' }) + '-' +
          returnDate.toLocaleDateString(locale, { year: 'numeric'});

        let matched = dateCompare.match(/([0-9]{2})\-([0-9]{2})\-([0-9]{4})/);

        // check to see if dateCompare is in MM-DD-YYYY and dateString is in YYYY-MM-DD
        if (
          matched &&
          dateString.match(/([0-9]{4})\-([0-9]{2})\-([0-9]{2})/)
        ) {
          dateCompare = matched[3] + '-' + matched[1] + '-' + matched[2];
        }

        timeOffset = parseInt(timeOffset);
        let offsetOperator = '+';
        // if date is previous date
        if (!!dateString && dateCompare + 'T00:00:00' !== dateString) {
          if (dateCompare + 'T00:00:00' < dateString) {
            timeOffset = 24 - timeOffset;
            offsetOperator = '-';
          }

          timeOffset = (timeOffset <= 9 ? '0' : '') + timeOffset + ':00';
          dateStringToCreate = dateString + offsetOperator + timeOffset;
          returnDate = new Date(dateStringToCreate);
        }
      }
      // cache string used to create the date
      _comUtilsDateCache[dateString] = dateStringToCreate;
    } else {
      // update to return today if datestring is null
      returnDate = !!dateString ? new Date(dateString) : COMUtils.getToday();
    }
    return returnDate;
  }

  /**
   * @desc get date from cookie || date today
   * note: checks to see if date is valid if coming from the cookie
   * @see https://stackoverflow.com/a/1353711
   * @return {Date} current date
   */
  static getToday() {
    let systemTime = COMUtils.getCookie('setSystemTime');
    if (systemTime) {
      let systemTimeDate = new Date(systemTime),
        isValidDate = Object.prototype.toString.call(systemTimeDate) === "[object Date]" && !isNaN(systemTimeDate.getTime());

      if (isValidDate) {
        return systemTimeDate;
      }
    }

    return new Date();
  }
}
